import { useMemo } from "react";
import { LocationService } from "@/services/LocationService";
import { useQuery } from "@tanstack/react-query";
import type { LocationFilters, LocationSort } from "@/types/Location";

interface UseLocationsQueryOptions {
  filters?: LocationFilters;
  sort?: LocationSort;
  enabled?: boolean;
  staleTime?: number;
  includeInactive?: boolean;
}

export function useLocationsQuery(options: UseLocationsQueryOptions = {}) {
  const {
    filters,
    sort,
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes
    includeInactive = false,
  } = options;

  const locationService = useMemo(() => new LocationService(), []);

  return useQuery({
    queryKey: ["locations", filters, sort, includeInactive],
    queryFn: async () => {
      const response = await locationService.getLocations(filters, sort, true, includeInactive);
      return response.data;
    },
    enabled,
    staleTime,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff up to 30 seconds
  });
}
